<template>
	<section class="w-full h-screen grid place-items-center px-4" data-bg>
		<ClientOnly>
			<form
				@submit.prevent="handleLogin"
				class="bg-white flex flex-col gap-4 border-2 border-black rounded-2xl p-6 w-full max-w-[400px]"
			>
				<img src="/logos/boxy-logo-light.png" alt="Boxy" width="136" height="40" class="mb-2" />
				<h2 class="text-2xl font-bold">Login</h2>

				<p class="text-gray-500 text-sm mb-4">
					Na prihlásenie zadajte email a heslo.
				</p>

				<div>
					<label for="email" class="font-bold text-sm">Email</label>
					<input
						class="w-full mt-2 p-2 border-2 rounded-md focus:border-black focus:ring focus:outline-none"
						type="email"
						id="email"
						v-model="form.email"
						placeholder="email@example.com"
						required
					/>
				</div>

				<div>
					<label for="password" class="font-bold text-sm">Heslo</label>
					<input
						type="password"
						id="password"
						required
						v-model="form.password"
						class="w-full mt-2 p-2 border-2 rounded-md focus:border-black focus:ring focus:outline-none"
					/>
				</div>

				<InlineMessage v-if="error" severity="error" :pt="messagePt">{{
					error?.data?.message ?? 'Nastala chyba'
				}}</InlineMessage>

				<Button :loading="isPending" size="lg" class="mt-4" type="submit">
					Prihlásiť sa
				</Button>
			</form>

			<template #fallback>
				<div
					class="bg-white flex flex-col gap-4 border-[2px] border-gray-300 rounded-md p-6 w-full max-w-[400px]"
				>
					<Skeleton class="max-w-[200px]" height="30px" />
					<Skeleton class="max-w-[100px] mb-6" />
					<Skeleton height="46px" />
					<Skeleton height="46px" />
					<Skeleton height="46px" class="mt-6" />
				</div>
			</template>
		</ClientOnly>
	</section>
</template>

<script setup>
import useAuthStore from '~/store/auth'
import useConfigStore from '~/store/config'
import { useMutation } from '@tanstack/vue-query'
import { useToast } from 'vue-toastification'
import useSocketStore from '~/store/socket'

const authStore = useAuthStore()
const configStore = useConfigStore()
const socketStore = useSocketStore()
const toast = useToast()

const form = ref({
	email: '',
	password: ''
})

const { mutate, isPending, error } = useMutation({
	mutationFn: (credentials) => authStore.login(credentials),
	onSuccess: async () => {
		await configStore.fetchLanguages()
		await configStore.fetchCurrencies()

		socketStore.initialize()
		toast.success('Boli ste úspešne prihlásený.')

		navigateTo({ path: '/' })
	}
})

const handleLogin = () => {
	mutate(form.value)
}

const messagePt = { text: { class: 'text-sm' } }

definePageMeta({
	layout: false
})

useSeoMeta({
	title: 'Prihlásenie'
})
</script>

<style scoped>
[data-bg] {
	background-color: #ffffff;
	background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='470' height='56.4' viewBox='0 0 1000 120'%3E%3Cg fill='none' stroke='%23E3E3E3' stroke-width='3.9' stroke-opacity='0.39'%3E%3Cpath d='M-500 75c0 0 125-30 250-30S0 75 0 75s125 30 250 30s250-30 250-30s125-30 250-30s250 30 250 30s125 30 250 30s250-30 250-30'/%3E%3Cpath d='M-500 45c0 0 125-30 250-30S0 45 0 45s125 30 250 30s250-30 250-30s125-30 250-30s250 30 250 30s125 30 250 30s250-30 250-30'/%3E%3Cpath d='M-500 105c0 0 125-30 250-30S0 105 0 105s125 30 250 30s250-30 250-30s125-30 250-30s250 30 250 30s125 30 250 30s250-30 250-30'/%3E%3Cpath d='M-500 15c0 0 125-30 250-30S0 15 0 15s125 30 250 30s250-30 250-30s125-30 250-30s250 30 250 30s125 30 250 30s250-30 250-30'/%3E%3Cpath d='M-500-15c0 0 125-30 250-30S0-15 0-15s125 30 250 30s250-30 250-30s125-30 250-30s250 30 250 30s125 30 250 30s250-30 250-30'/%3E%3Cpath d='M-500 135c0 0 125-30 250-30S0 135 0 135s125 30 250 30s250-30 250-30s125-30 250-30s250 30 250 30s125 30 250 30s250-30 250-30'/%3E%3C/g%3E%3C/svg%3E");
}
</style>
